/**
 * What a survey question is CALLED, long and short.
 *
 * ── A catalog question's wire label is English on purpose ────────────────────────────────────
 * `StandardQuestionCatalog.cs` stores the catalog's own default — „Body weight (kg)", „Date of
 * birth" — and `SurveyQuestionResolver.ResolveStandardView` copies that `DefaultLabel` onto every
 * view, including the FROZEN snapshot a filled survey is read back from. It records that every
 * client renders `intakeSurvey.q.<code>.head` instead, and the phone does. So a frozen English
 * label is not what the client was asked; it is the catalog's identifier wearing words.
 *
 * Where there is a code, the code is the name: `q.<code>.head` is the question as it is ASKED and
 * `qLabel.<code>` is its one or two words. A question with a short form and NO head falls back to
 * the short form rather than to the wire — the intake's demographic five are exactly that case. Those keys live in the VERBATIM mobile locales
 * (`shared/locales/{pl,en}.json`, never edited here and overwritten by a re-sync), which is why
 * they carry no `web:` prefix.
 *
 * A question the trainer wrote themselves has no code and keeps their wording — it is the only name
 * that exists for it, and the one case where the stored label really is what was asked.
 *
 * ── Promoted out of `pages/client-detail` on its second consumer, 17 September 2026 ──────────
 * It lived in `client-detail/model/surveyHistory.ts` while the survey history was the only surface
 * naming a coded question. `pages/reports` is the second — its Ankiety card, its editor, its phone
 * preview and its review pane all name the same catalog — and a `pages` slice may not reach into
 * another one. The client detail's behaviour is unchanged: it still reads `label` and `shortLabel`
 * with exactly these fallbacks.
 */

/**
 * The narrowest `t` this needs: a key in, a string out. No interpolation — a question's name is
 * never counted and never has a value spliced into it.
 */
export type TranslateQuestionKey = (key: string) => string;

export interface QuestionNames {
  /**
   * The question as it is ASKED, on one line. The head carries a hard line break — „Ile dziś\nważysz?"
   * is wrapped for a phone's question card — and a row is one line, so the break collapses to a space.
   */
  readonly label: string;
  /** Its one or two words. Falls back to `label`, so it is never shorter than nothing. */
  readonly shortLabel: string;
}

/** What one question carries that its name can be read from. */
export interface NameableQuestion {
  /** The STANDARD catalog's code — `checkin_weight`, `dob` — or absent on a trainer's own question. */
  readonly code?: string | null;
  /** The wire's own label. The last resort, and the only name a custom question has. */
  readonly label: string;
}

export function questionNames(
  t: TranslateQuestionKey,
  question: NameableQuestion,
): QuestionNames {
  const code = question.code ?? null;
  const head = code === null ? null : translated(t, `intakeSurvey.q.${code}.head`);
  const short = code === null ? null : translated(t, `intakeSurvey.qLabel.${code}`);
  // The SHORT form before the wire's own label, and that order is the fix for three questions that
  // printed in English. The catalog carries `qLabel.<code>` for `dob`, `gender`, `goal`, `height`
  // and `initial_weight` but no `q.<code>.head` for any of them — they are asked by the phone's
  // onboarding screens rather than by a question card, so no head was ever written. Falling straight
  // through to `question.label` printed `StandardQuestionCatalog.cs`'s English default („Date of
  // birth", „Gender", „Your training goal") on a Polish screen. The wire's label stays the last
  // resort, where it is the only name a custom question has.
  //
  // Between the mobile catalog and the wire sits the WEB's own word for a code the verbatim mobile
  // locale has no name for at all — `checkin_sleep_hours` printed „Hours of sleep" on a Polish
  // screen. It lives in `web:surveyQuestions.<code>`, so a re-sync of the mobile file never drops it,
  // and a later mobile name for the same code still wins.
  const webName = code === null ? null : translated(t, `web:surveyQuestions.${code}`);
  const label =
    head === null ? (short ?? webName ?? question.label) : head.replace(/\s*\n\s*/g, ' ');
  return { label, shortLabel: short ?? label };
}

/**
 * One translation, or `null` when this build has no word for the key.
 *
 * i18next hands the KEY back for a miss, so „intakeSurvey.qLabel.squat_clip" is what a missing
 * entry looks like — and printing that as a question's name is worse than printing the English the
 * wire sent. An empty string is a miss too: the mobile catalog carries blank `label` and `hint`
 * members beside the heads it does fill.
 */
function translated(t: TranslateQuestionKey, key: string): string | null {
  const text = t(key);
  // A namespaced miss comes back WITHOUT its `web:` prefix, so both spellings of the key are a miss.
  const bare = key.slice(key.indexOf(':') + 1);
  return text === key || text === bare || text.trim() === '' ? null : text;
}

/**
 * Where a catalog code's choices are NAMED: the locale prefix its enum members sit under.
 *
 * Only the codes that HAVE words, and only where the name differs from the code or would otherwise
 * have to be guessed. A code with no entry keeps the wire's own word, which is the safe direction:
 * a miss prints English rather than printing the wrong Polish.
 *
 * `equipment` is the one whose words are not the intake's: the resolver offers the LIVE `Equipment`
 * taxonomy (`StandardQuestionCatalog.EquipmentCode`), so an option is an equipment code —
 * `resistanceBand`, `pullUpBar` — and the exercise library already names every one of them at
 * `exerciseLibrary.equipment.<code>`. A row added to the taxonomy after the last locale sync has no
 * word yet and falls back to its code, exactly as any other miss does.
 *
 * Promoted out of `pages/reports/model/reviewQueue.ts` on 27 September 2026, when the public form's
 * question runner became the second surface naming a catalog question's options.
 */
const OPTION_PREFIXES: Readonly<Record<string, string>> = {
  gender: 'intakeSurvey.opt.gender',
  activity_level: 'intakeSurvey.opt.activity',
  experience: 'intakeSurvey.opt.experience',
  availability: 'intakeSurvey.opt.availability',
  diet: 'intakeSurvey.opt.diet',
  equipment: 'exerciseLibrary.equipment',
};

/** Whether a catalog code's options are enum members with words of their own. */
export function hasStandardOptionLabels(code: string | null | undefined): boolean {
  return code != null && OPTION_PREFIXES[code] !== undefined;
}

/**
 * What one offered option of a catalog question is CALLED. A choice arrives as its ENUM member —
 * „Male", „Sedentary", „resistanceBand" — and the mobile locales carry the words under the code's
 * prefix, verbatim and shared, so they are read rather than reinvented. A trainer's own question
 * has no code, and its options are the trainer's own words.
 */
export function standardOptionLabel(
  t: TranslateQuestionKey,
  code: string | null | undefined,
  value: string,
): string {
  const prefix = code == null ? undefined : OPTION_PREFIXES[code];
  if (prefix === undefined) return value;
  return translated(t, `${prefix}.${value}`) ?? value;
}
